import { allowWidgetParam } from '@core/params'

import type { BaseElement } from './BaseElement'
import type { Constructor } from './types'

/**
 * Mixin providing rerender when attributes change.
 *
 * Docs on mixins: https://lit.dev/docs/composition/mixins/
 * @param superClass
 * @returns
 */
export const MutationMixin = <T extends Constructor<BaseElement>>(
  superClass: T,
) => {
  class MutationMixinClass extends superClass {
    #mutationObserver: MutationObserver | null = null

    /**
     * Initializing the observer when component is added to the document's DOM.
     */
    override connectedCallback(): void {
      super.connectedCallback()

      this.#mutationObserver = new MutationObserver((mutations) => {
        if (
          mutations
            .map((m) => m.attributeName)
            .some((n) => n && allowWidgetParam(n))
        ) {
          this.onAttributeChange()
        }
      })
      this.#mutationObserver.observe(this, { attributes: true })
    }

    /**
     * Disconnecting the observer when component is removed from the document's DOM.
     */
    override disconnectedCallback(): void {
      super.disconnectedCallback()
      this.#mutationObserver?.disconnect()
    }

    /**
     * Noop method to be overridden by the component.
     */
    protected onAttributeChange(): void {
      // noop
    }
  }

  return MutationMixinClass
}
